import { useState, useCallback } from 'react';
import ordersService, { CheckDeadlineItem, QualifyingDetail } from '../services/orders.service';
import { ReasonNoteResult } from '../components/ReasonNoteModal';

export interface DeadlineCheckResult {
  qualifyingPros: number[];
  qualifyingDetails: QualifyingDetail[];
  category_id: number | null;
  category_name: string | null;
  reason_id: number | null;
  reason_name: string | null;
  note: string | null;
}

interface DeadlineCheckState {
  isChecking: boolean;
  showNoteModal: boolean;
  qualifyingPros: number[];
  qualifyingDetails: QualifyingDetail[];
}

/**
 * Hook to check deadline criteria before removing a driver from moves.
 *
 * Usage:
 *   const { checkAndPrompt, showNoteModal, handleNoteSubmit, handleNoteCancel, isChecking } = useDeadlineCheck();
 *
 *   const result = await checkAndPrompt(items);
 *   if (result === null) return; // user cancelled
 *   // result.note is null when no deadline items qualified (proceed without notes)
 *   // result.note is a string when user filled out the form
 */
export function useDeadlineCheck() {
  const [state, setState] = useState<DeadlineCheckState>({
    isChecking: false,
    showNoteModal: false,
    qualifyingPros: [],
    qualifyingDetails: [],
  });

  const [pendingResolve, setPendingResolve] = useState<((value: DeadlineCheckResult | null) => void) | null>(null);

  const checkAndPrompt = useCallback(async (
    items: CheckDeadlineItem[]
  ): Promise<DeadlineCheckResult | null> => {
    setState(prev => ({ ...prev, isChecking: true }));

    try {
      const response = await ordersService.checkDeadlineCriteria(items);

      if (!response.requiresNote) {
        setState(prev => ({ ...prev, isChecking: false }));
        return { qualifyingPros: [], qualifyingDetails: [], category_id: null, category_name: null, reason_id: null, reason_name: null, note: null };
      }

      // Show the modal and wait for user input
      return new Promise((resolve) => {
        setPendingResolve(() => resolve);
        setState({
          isChecking: false,
          showNoteModal: true,
          qualifyingPros: response.qualifyingPros,
          qualifyingDetails: response.qualifyingDetails,
        });
      });
    } catch (error) {
      console.error('Failed to check deadline criteria:', error);
      setState(prev => ({ ...prev, isChecking: false }));
      // On error, allow the operation to proceed without a note
      return { qualifyingPros: [], qualifyingDetails: [], category_id: null, category_name: null, reason_id: null, reason_name: null, note: null };
    }
  }, []);

  const handleNoteSubmit = useCallback((result: ReasonNoteResult) => {
    if (pendingResolve) {
      pendingResolve({
        qualifyingPros: state.qualifyingPros,
        qualifyingDetails: state.qualifyingDetails,
        category_id: result.category_id,
        category_name: result.category_name,
        reason_id: result.reason_id,
        reason_name: result.reason_name,
        note: result.note,
      });
      setPendingResolve(null);
    }
    setState(prev => ({ ...prev, showNoteModal: false, qualifyingPros: [], qualifyingDetails: [] }));
  }, [pendingResolve, state.qualifyingPros, state.qualifyingDetails]);

  const handleNoteCancel = useCallback(() => {
    if (pendingResolve) {
      pendingResolve(null); // Signal cancellation
      setPendingResolve(null);
    }
    setState(prev => ({ ...prev, showNoteModal: false, qualifyingPros: [], qualifyingDetails: [] }));
  }, [pendingResolve]);

  return {
    checkAndPrompt,
    showNoteModal: state.showNoteModal,
    handleNoteSubmit,
    handleNoteCancel,
    isChecking: state.isChecking,
  };
}
